import React, { Component } from "react";
import styled from "styled-components";
import moment from "moment";

import { PricingDialog } from "js/react/views/Onboarding/Components/Pricing";
import { OnboardingPageStructure } from "js/react/views/Onboarding/Components/Shared";
import { WorkspacePlanTier } from "common/interfaces";
import AppController from "js/core/AppController";
import { ShowDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import Analytics from "js/analytics";
import stripeProducts from "common/stripeProducts";
import { t } from "js/i18n";
import type { OnSuccessParams } from "js/controllers/CheckoutController";

const Container = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1100;
`;

class PlanPreviewEndedDialog extends Component<{ onClose: () => void, canManageBilling: boolean }, { onClose: () => void }> {
    componentDidMount() {
        Analytics.PlanPreview.PlanPreviewEndedDialogOpened();
    }

    handleShowSuccessfulPurchaseDialog = async (targetTier: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM, trialDays: number) => {
        Analytics.ModifyWorkspacePlan.UpgradeFlowComplete();

        // @ts-ignore
        const { default: SuccessfulPurchaseDialog } = await import("js/react/views/Billing/dialogs/SuccessfulPurchaseDialog");

        const successDialog = ShowDialog(SuccessfulPurchaseDialog, {
            productDisplayName: stripeProducts[targetTier].productName,
            callToActionText: t("Continue"),
            showTrial: trialDays > 0,
            trialDurationDays: trialDays,
            trialExpirationDate: moment().add(trialDays, "days").format("MMMM Do YYYY"),
            onCallToActionClick: () => {
                successDialog.close();
            }
        });
    };

    handleCheckout = async (plan: WorkspacePlanTier.PRO | WorkspacePlanTier.TEAM, interval: "year" | "month") => {
        const { onClose, canManageBilling } = this.props;

        Analytics.PlanPreview.PlanPreviewEndedDialogUpgradeClicked({ plan, interval });

        if (!canManageBilling) {
            ShowMessageDialog({
                title: t("Workspace admin action required"),
                message: t("You do not have permission to upgrade the workspace plan. Ask your workspace admin to upgrade to a paid plan to continue using Beautiful.ai."),
                confirmButtonText: t("Ok"),
                onConfirm: () => { }
            });
            return;
        }

        const targetTier = plan === "team" ? WorkspacePlanTier.TEAM : WorkspacePlanTier.PRO;

        const shouldShowSuccessfulPurchaseDialog = targetTier === WorkspacePlanTier.PRO && AppController.user.hasNeverTakenAnyTrial?.();
        const shouldShowInviteUsersDialog = targetTier === WorkspacePlanTier.TEAM;

        // @ts-ignore
        const { CheckoutDialog } = await import("js/react/views/Billing/Checkout/Checkout");
        // @ts-ignore
        const { InviteUsersDialog } = await import("js/react/views/UserOptions/panes/ManageUsersPane/Components/InviteUsersDialog");

        ShowDialog(CheckoutDialog, {
            tier: targetTier,
            billingInterval: interval,
            workspaceId: AppController.workspaceId,
            onClose: (success: boolean, params?: OnSuccessParams) => {
                if (!success) {
                    return;
                }

                Analytics.PlanPreview.PlanPreviewEndedDialogUpgradeSuccess({ plan: params.tier, interval: params.interval });

                onClose();

                if (shouldShowInviteUsersDialog) {
                    ShowDialog(InviteUsersDialog, {
                        cancelButtonText: t("Invite later"),
                        showSelf: true,
                        seatsInfoVariant: "invite-only",
                        capAtPurchasedSeats: true
                    });
                    return;
                }

                if (shouldShowSuccessfulPurchaseDialog) {
                    this.handleShowSuccessfulPurchaseDialog(targetTier, params.trialDays);
                    return;
                }
            }
        });
    }

    render() {
        return (
            <Container data-test-id="plan-preview-ended-dialog">
                <OnboardingPageStructure initialized={true} hideHeaderAlways={true} hideHeaderMobile={true} opaqueBackground={false}>
                    <PricingDialog
                        fullWidth={false}
                        header="Enjoying Beautiful.ai? Continue with our free 14 day trial..."
                        startCheckout={this.handleCheckout}
                        onMounted={() => { }}
                    />
                </OnboardingPageStructure>
            </Container>
        );
    }
}

export default PlanPreviewEndedDialog;
